// Pure helpers of the « Studio » screens (web/ui/studio.js, docs/wip/SPEC-STUDIO.md §3): the
// studios of a floor from the office data, the type tabs (same `kind` of several services
// together), status labels and colours, the list query, the breadcrumb, children grouped by
// type, the columns of the tree view and the « #K7Q2 » label of a short reference. DOM-free, so node:test covers them.

/** Colours of a status chip (web/styles/studio.css .st-tone-*). */
export const TONES = Object.freeze(['neutral', 'info', 'amber', 'success', 'danger']);
/** Sort orders of the list (server/studio/service.js SORTS). */
export const SORTS = Object.freeze(['recent', 'old', 'status', 'title']);
/** Elements asked per page of the list. */
export const PAGE_SIZE = 120;

/**
 * Is the studio of a floor switched on (Administration → Étages & services, `floor.studio`)?
 * @param {{floors?: object[]}|null|undefined} office the store's `office` (bootstrap)
 * @param {string|null|undefined} floorId
 * @returns {boolean}
 */
export function floorHasStudio(office, floorId) {
  return (office?.floors || []).some((f) => f && f.id === floorId && f.studio === true);
}

/**
 * The studio of a department — mirror of server/studio/templates.js deptStudio, the one rule:
 * none when its floor's studio is off; else its own types (`dept.studio.pipeline`), else the
 * default ones (bootstrap `office.studioDefault`).
 * @param {{floors?: object[], studioDefault?: {template: string, pipeline: object[]}|null}|null|undefined} office
 * @param {object|null|undefined} dept
 * @returns {{template: string|null, pipeline: object[], own: boolean}|null}
 */
export function deptStudio(office, dept) {
  if (!dept || !floorHasStudio(office, dept.floorId)) return null;
  const mine = dept.studio && Array.isArray(dept.studio.pipeline) && dept.studio.pipeline.length ? dept.studio : null;
  if (mine) return { template: mine.template || null, pipeline: mine.pipeline, own: true };
  const def = office?.studioDefault;
  return def && Array.isArray(def.pipeline) && def.pipeline.length ? { template: def.template || null, pipeline: def.pipeline, own: false } : null;
}

/**
 * A department with the studio it has as `studio` (see deptStudio), or null when it has none.
 * @param {object|null|undefined} office the store's `office`
 * @param {string|null|undefined} deptId
 * @returns {object|null}
 */
export function studioDept(office, deptId) {
  const dept = (office?.departments || []).find((d) => d && d.id === deptId) || null;
  const studio = deptStudio(office, dept);
  return studio ? { ...dept, studio } : null;
}

/**
 * The departments of a floor with their studio (see studioDept): every one the person sees when
 * the floor's studio is on, none otherwise. The services with types of their own come first (a
 * « Création Ads » before a « Direction » on the default types), each group in the floor's order:
 * they are the ones « Nouvelle pub » and « Nouveau » preselect and whose labels name the shared tabs.
 * @param {object|null|undefined} office the store's `office` (bootstrap: floors, departments, studioDefault)
 * @param {string|null|undefined} floorId
 * @returns {object[]}
 */
export function studioDepts(office, floorId) {
  if (!floorHasStudio(office, floorId)) return [];
  return (office?.departments || [])
    .filter((d) => d && d.floorId === floorId)
    .map((d) => studioDept(office, d.id))
    .filter(Boolean)
    .sort((a, b) => Number(b.studio.own) - Number(a.studio.own)
      || (a.order ?? 0) - (b.order ?? 0) || String(a.name).localeCompare(String(b.name), 'fr'));
}

/**
 * The type tabs of a view: the types of every studio shown, grouped by `kind` (the first service
 * names it), the main types first, each group in pipeline order; `main` when one of the
 * services marks it so.
 * @param {object[]} depts studio departments shown
 * @param {{all?: boolean}} [opts] all: the steps too (labels of children, tree columns)
 * @returns {Array<{kind: string, label: string, plural: string, main: boolean, step: boolean, depts: string[]}>}
 */
export function kindTabs(depts, { all = false } = {}) {
  const out = [];
  for (const d of depts || []) {
    (d.studio?.pipeline || []).forEach((type) => {
      let tab = out.find((x) => x.kind === type.kind);
      if (!tab) {
        tab = { kind: type.kind, label: type.label || type.kind, plural: type.plural || type.label || type.kind, main: false, step: false, depts: [] };
        out.push(tab);
      }
      if (type.main) tab.main = true;
      if (type.step || STEP_KINDS.includes(type.kind)) tab.step = true;
      tab.depts.push(d.id);
    });
  }
  // The production steps (competitor's ad, scenes, start images: `step`) live inside the page of
  // their ad, not as tabs; they stay tabs only when a studio has nothing else.
  const shown = all || !out.some((x) => !x.step) ? out : out.filter((x) => !x.step);
  // The first main type (« Nos pubs ») first, then the others in pipeline order (séquences,
  // casting, produits); with the steps shown, every main type first.
  if (shown === out) return [...shown.filter((x) => x.main), ...shown.filter((x) => !x.main)];
  // A main type hidden as a step (the brief of a studio set up by hand, main by choice or as its
  // first type) hands its place to « Nos pubs », where it now lives: first and opened first, even
  // when the pub type is not marked main (else the last type, « Produits », would open).
  const pub = out.some((x) => x.step && x.main) ? shown.find((x) => x.kind === 'pub') || null : null;
  if (pub) pub.main = true;
  const first = pub || shown.find((x) => x.main);
  return first ? [first, ...shown.filter((x) => x !== first)] : shown;
}

/**
 * Kinds that are always production steps, even in a studio set up by hand without `step` (the
 * competitor's ad, the brief, the scenes, the start images): shown inside their ad's page, never
 * as tabs (« Nouvelle pub » on « Nos pubs » creates a brief with its pub).
 */
export const STEP_KINDS = Object.freeze(['source', 'brief', 'scene', 'image']);

/** The tab shown first: the first main type (else the first type). */
export function defaultKind(tabs) {
  return (tabs.find((x) => x.main) || tabs[0])?.kind || null;
}

/** The pipeline type of a department given by studioDept / studioDepts (null when unknown). */
export function typeOf(dept, kind) {
  return (dept?.studio?.pipeline || []).find((t) => t.kind === kind) || null;
}

/**
 * A status of a type with its colour: its own tone, else error → danger, final → success, the
 * first one → neutral, others → info (same rule as the server). Unknown keys stay readable.
 * @param {object|null} type
 * @param {string} key
 * @returns {{key: string, label: string, tone: string, final: boolean, error: boolean, pending: boolean}}
 */
export function statusInfo(type, key) {
  const list = Array.isArray(type?.statuses) ? type.statuses : [];
  const i = list.findIndex((s) => s.key === key);
  if (i < 0) return { key, label: String(key ?? '—'), tone: 'neutral', final: false, error: false, pending: true };
  const s = list[i];
  const tone = TONES.includes(s.tone) ? s.tone : s.error ? 'danger' : s.final ? 'success' : i === 0 ? 'neutral' : 'info';
  return { key: s.key, label: s.label || s.key, tone, final: !!s.final, error: !!s.error, pending: !s.final && !s.error };
}

/**
 * The status chips of a tab: every status of the type in the services shown (same key → one
 * chip, the first label), with the counts of the list answer.
 * @param {object[]} depts
 * @param {string} kind
 * @param {Record<string, number>} counts
 * @returns {Array<{key: string, label: string, tone: string, count: number}>}
 */
export function statusChips(depts, kind, counts = {}) {
  const out = [];
  for (const d of depts || []) {
    const type = typeOf(d, kind);
    for (const s of type?.statuses || []) {
      if (out.some((x) => x.key === s.key)) continue;
      const info = statusInfo(type, s.key);
      out.push({ key: s.key, label: info.label, tone: info.tone, count: Number(counts?.[s.key]) || 0 });
    }
  }
  // Statuses found in the counts that no pipeline names any more (renamed): still shown.
  for (const [key, n] of Object.entries(counts || {})) if (!out.some((x) => x.key === key)) out.push({ key, label: key, tone: 'neutral', count: Number(n) || 0 });
  return out;
}

/**
 * Query string of GET /api/studio/floors/:floorId/items for the view's state.
 * @param {{deptId?: string|null, kind?: string|null, status?: string|null, agentId?: string, tag?: string,
 *   search?: string, sort?: string, limit?: number, offset?: number}} s
 * @returns {Record<string, string|number>}
 */
export function listParams(s) {
  const out = {};
  if (s.deptId) out.deptId = s.deptId;
  if (s.kind) out.kind = s.kind;
  if (s.status) out.status = s.status;
  if (s.agentId) out.agentId = s.agentId;
  if (s.tag) out.tag = s.tag;
  const q = String(s.search || '').trim();
  if (q) out.search = q.slice(0, 200);
  if (s.sort && SORTS.includes(s.sort) && s.sort !== 'recent') out.sort = s.sort;
  out.limit = s.limit || PAGE_SIZE;
  if (s.offset) out.offset = s.offset;
  return out;
}

/**
 * « 3 scènes · 2 images » from an element's children counts, in pipeline order.
 * @param {Record<string, number>} counts
 * @param {Array<{kind: string, label: string, plural: string}>} tabs
 * @returns {string}
 */
export function childrenLine(counts, tabs) {
  const parts = [];
  const seen = new Set();
  for (const tab of tabs || []) {
    const n = Number(counts?.[tab.kind]) || 0;
    seen.add(tab.kind);
    if (n) parts.push(`${n} ${(n > 1 ? tab.plural : tab.label).toLocaleLowerCase('fr')}`);
  }
  for (const [kind, n] of Object.entries(counts || {})) if (!seen.has(kind) && n) parts.push(`${n} ${kind}`);
  return parts.join(' · ');
}

/**
 * The breadcrumb of an element: its first parent, that one's first parent… (the chain up to a
 * top element), oldest first. `known` maps ids to summaries (the element's parents, tree nodes).
 * @param {object} item detail (parents: summaries)
 * @param {Map<string, object>} known
 * @param {number} [max]
 * @returns {object[]} summaries, top first
 */
export function crumbChain(item, known, max = 8) {
  const chain = [];
  const seen = new Set([item?.id]);
  let cur = item?.parents?.[0] || null;
  while (cur && chain.length < max && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    const next = Array.isArray(cur.parents) ? cur.parents.map((p) => (typeof p === 'string' ? known.get(p) : p)).find(Boolean) : null;
    cur = next || null;
  }
  return chain;
}

/**
 * Children grouped by type, in the order of the tabs (then any other type).
 * @param {object[]} children summaries
 * @param {Array<{kind: string}>} tabs
 * @returns {Array<{kind: string, items: object[]}>}
 */
export function groupByKind(children, tabs) {
  const groups = new Map();
  for (const c of children || []) {
    if (!groups.has(c.kind)) groups.set(c.kind, []);
    groups.get(c.kind).push(c);
  }
  const order = (tabs || []).map((x) => x.kind);
  return [...groups.entries()]
    .sort(([a], [b]) => (order.indexOf(a) < 0 ? 99 : order.indexOf(a)) - (order.indexOf(b) < 0 ? 99 : order.indexOf(b)))
    .map(([kind, items]) => ({ kind, items }));
}

/**
 * The tree view of an element: one row per direct child (a scene of a competitor ad), one column
 * per type found below it (image → séquence → pub), each cell the descendants of that row of
 * that type (a pub made from two scenes appears in both rows).
 * @param {{down?: object[]}} root tree node (GET /api/studio/items/:id/tree)
 * @param {Array<{kind: string}>} tabs pipeline order
 * @returns {{columns: string[], rows: Array<{head: object, cells: Record<string, object[]>}>}}
 */
export function treeRows(root, tabs) {
  const order = (tabs || []).map((x) => x.kind);
  const rank = (k) => (order.indexOf(k) < 0 ? 99 : order.indexOf(k));
  const kinds = new Set();
  const all = (root?.down || []).map((head) => {
    const cells = {};
    const seen = new Set([head.id]);
    const walk = (node) => {
      for (const c of node.down || []) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        (cells[c.kind] ||= []).push(c);
        walk(c);
      }
    };
    walk(head);
    return { head, cells, ids: seen };
  });
  // A direct child already shown under another one (our final ad, child of the source and of a
  // sequence) gets no row of its own.
  const rows = all.filter((r) => !all.some((o) => o !== r && o.ids.has(r.head.id))).map(({ head, cells }) => {
    kinds.add(head.kind);
    for (const k of Object.keys(cells)) kinds.add(k);
    return { head, cells };
  });
  return { columns: [...kinds].sort((a, b) => rank(a) - rank(b)), rows };
}

/**
 * How an element's short reference is shown and copied: « #K7Q2 » ('' without one).
 * @param {unknown} ref
 * @returns {string}
 */
export function refLabel(ref) {
  const r = typeof ref === 'string' ? ref.trim().replace(/^#/, '').toUpperCase() : '';
  return /^[A-Z0-9]{4}$/.test(r) ? `#${r}` : '';
}

/** Tags found in a page of elements (for the tag filter), sorted. */
export function tagsOf(items) {
  const set = new Set();
  for (const it of items || []) for (const tg of it.tags || []) set.add(tg);
  return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
}

/** Does a live `studio` event concern a view (floor, optional service)? */
export function eventMatches(ev, { floorId, deptId = null } = {}) {
  if (!ev || ev.floorId !== floorId) return false;
  return !deptId || ev.deptId === deptId;
}

/**
 * Where a person may create an element of a tab from the list: the services shown whose type of
 * that kind has no parent type (a competitor ad, a brief, a person — the others come from their
 * parents) and on which the person may operate.
 * @param {object[]} depts studio departments shown
 * @param {string|null} kind
 * @param {(dept: object) => boolean} canOperate
 * @returns {object[]} departments
 */
export function creatableDepts(depts, kind, canOperate) {
  if (!kind) return [];
  return (depts || []).filter((d) => {
    const type = typeOf(d, kind);
    return type && !(type.parentKinds || []).length && canOperate(d);
  });
}

/**
 * The services where « Nouvelle pub » creates a brief and its pub (POST /api/studio/depts/:id/ads):
 * their `pub` type accepts a brief as parent and their `brief` type has no parent type; never a
 * competitor-ads studio (its pubs come from the competitor's ads). Only where the person operates.
 * @param {object[]} depts studio departments shown
 * @param {(dept: object) => boolean} canOperate
 * @returns {object[]} departments
 */
export function adDepts(depts, canOperate) {
  return (depts || []).filter((d) => {
    const brief = typeOf(d, 'brief');
    const pub = typeOf(d, 'pub');
    return brief && pub && !(brief.parentKinds || []).length && (pub.parentKinds || []).includes('brief') && canOperate(d);
  });
}

/** The fields of a brief the owner fills in « Nouvelle pub » (when its type has them): the idea first. */
export const AD_FIELDS = Object.freeze(['brief', 'product', 'audience', 'message', 'references']);

/**
 * File roles offered when adding files: a person's photos, a product's photos (one per colour), a
 * brief's references (casting, product), a hook's video (the result), else a final file or a reference.
 */
export const FILE_ROLES = Object.freeze({
  person: Object.freeze(['base', 'visage', 'porte']),
  product: Object.freeze(['photo', '', 'reference']),
  brief: Object.freeze(['reference', '']),
  hook: Object.freeze(['final', '']),
  other: Object.freeze(['', 'final', 'reference']),
});

/** The roles offered for a kind (keys; '' = no role). */
export function fileRoles(kind) {
  return kind !== 'other' && Object.hasOwn(FILE_ROLES, kind) ? FILE_ROLES[kind] : FILE_ROLES.other;
}
